import { z } from 'zod';
import { create } from 'zustand';
// eslint-disable-next-line no-restricted-imports -- legacy: put-away in-app alerts stay on the device until the server keeps them per person (backend ask)
import { persist } from 'zustand/middleware';
import type { PersistStorage, StorageValue } from 'zustand/middleware';

import { IN_APP_FRESH_MS } from '../domain/inAppAlerts';

export const DISMISSED_STORAGE_KEY = 'ctl-in-app-dismissed';
const STORAGE_VERSION = 1;

type DismissedAt = Readonly<Record<string, number>>;

interface InAppSessionState {
  readonly openedAt: number;
  readonly dismissed: ReadonlySet<string>;
  readonly dismissedAt: DismissedAt;
  readonly seenLive: ReadonlySet<string>;
  readonly actions: {
    readonly dismiss: (key: string) => void;
    readonly sawLive: (key: string) => void;
  };
}

type PersistedSession = Pick<InAppSessionState, 'dismissedAt'>;

const initial = (): Omit<InAppSessionState, 'actions'> => ({
  openedAt: Date.now(),
  dismissed: new Set<string>(),
  dismissedAt: {},
  seenLive: new Set<string>(),
});

const keepEntries = (entries: Readonly<Record<string, unknown>>, keep: (at: number) => boolean): DismissedAt =>
  Object.entries(entries).reduce<Record<string, number>>((kept, [key, at]) => {
    if (typeof at === 'number' && Number.isFinite(at) && keep(at)) kept[key] = at;
    return kept;
  }, {});

const stillRelevant = (dismissedAt: DismissedAt, now: number): DismissedAt =>
  keepEntries(dismissedAt, (at) => now - at <= IN_APP_FRESH_MS);

const persistedSchema = z.object({
  dismissedAt: z.record(z.unknown()).transform((entries) => keepEntries(entries, () => true)),
});

const browserStorage = (): Storage | null => {
  try {
    // eslint-disable-next-line no-restricted-properties -- legacy: put-away in-app alerts stay on the device until the server keeps them per person (backend ask)
    return window.localStorage;
  } catch {
    return null;
  }
};

const readJson = (text: string | null): StorageValue<PersistedSession> | null => {
  if (text === null) return null;
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === 'object' && value !== null ? (value as StorageValue<PersistedSession>) : null;
  } catch {
    return null;
  }
};

const dismissedStorage: PersistStorage<PersistedSession> = {
  getItem: (name) => {
    try {
      return readJson(browserStorage()?.getItem(name) ?? null);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      browserStorage()?.setItem(name, JSON.stringify(value));
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      browserStorage()?.removeItem(name);
    } catch {
      return;
    }
  },
};

const restore = (persisted: unknown, current: InAppSessionState): InAppSessionState => {
  const parsed = persistedSchema.safeParse(persisted);
  if (!parsed.success) return { ...current, dismissed: new Set<string>(), dismissedAt: {} };
  const dismissedAt = stillRelevant(parsed.data.dismissedAt, Date.now());
  return { ...current, dismissedAt, dismissed: new Set(Object.keys(dismissedAt)) };
};

export const useInAppSessionStore = create<InAppSessionState>()(
  persist(
    (set) => ({
      ...initial(),
      actions: {
        dismiss: (key) =>
          set((state) => {
            const now = Date.now();
            const dismissedAt = { ...stillRelevant(state.dismissedAt, now), [key]: now };
            return { dismissedAt, dismissed: new Set(state.dismissed).add(key) };
          }),
        sawLive: (key) => set((state) => (state.seenLive.has(key) ? state : { seenLive: new Set(state.seenLive).add(key) })),
      },
    }),
    {
      name: DISMISSED_STORAGE_KEY,
      version: STORAGE_VERSION,
      storage: dismissedStorage,
      partialize: (state): PersistedSession => ({ dismissedAt: state.dismissedAt }),
      merge: restore,
    },
  ),
);

export const useInAppOpenedAt = (): number => useInAppSessionStore((state) => state.openedAt);
export const useDismissedAlerts = (): ReadonlySet<string> => useInAppSessionStore((state) => state.dismissed);
export const useSeenLiveAlerts = (): ReadonlySet<string> => useInAppSessionStore((state) => state.seenLive);
export const useInAppSessionActions = (): InAppSessionState['actions'] => useInAppSessionStore((state) => state.actions);

export const resetInAppSession = (): void => useInAppSessionStore.setState(initial());
